/*
  The Boardium mark, redrawn as vector from the artwork in docs/.

  Two shapes: a sail and a wedge, sharing one diagonal. The sail closes on a circle of
  radius 43 centred at (23, 41); the wedge on one of radius 25.6 centred at (37.4, 50.2).
  Traced off the supplied raster and checked against it at 3072px wide, where the aspect
  ratio matches to four significant figures and the apex, the kink and both arcs land
  within a pixel.

  Order matters and is the whole of the colour construction: the wedge is painted first
  and the sail sits on top of it, so navy shows only where the sail is not. There is no
  third fill for the overlap. The previous artwork had one; this one does not, and drawing
  the two shapes in the other order would put a navy triangle across the middle of it.

  REVERSAL. The supplied artwork is navy on a near-white ground, so on the navy sidebar
  the wedge would disappear into its own background. on-navy swaps that one fill for white
  and leaves the gold alone, which is the ordinary way a mark is reversed out and keeps
  the shape reading as the same shape.
*/

const SAIL = 'M 35 0 L 22 21 L 56.1 59 L 62.1 59 A 43 43 0 0 0 35 0 Z'
const WEDGE = 'M 0 59 L 20 24.6 L 37.4 24.6 A 25.6 25.6 0 0 1 61.4 59 Z'

/*
  Sampled from the artwork as #D7AB42 and #11284A, then snapped to the brand's own
  #D4AF37 and #0B2545. The difference is imperceptible on its own and the snap matters:
  a wedge one shade off the sidebar it sits on reads as a rendering fault rather than as
  a logo.
*/
const GOLD = '#D4AF37'
const NAVY = '#0B2545'

export type MarkVariant = 'on-light' | 'on-navy'

export function Mark({
  variant = 'on-light', live = false, className,
}: {
  variant?: MarkVariant
  /*
    A meeting is running. The brief for this asked for the seats to fill one at a time,
    which belonged to an earlier mark that had five dots in it. This artwork has two
    shapes and no dots, so the ambient signal is a slow breath on the gold instead:
    2.4 seconds, low amplitude, and only ever while a meeting is actually running. Loud
    enough to notice from across a workshop, quiet enough not to read as a fault.
  */
  live?: boolean
  className?: string
}) {
  return (
    <svg
      className={`mark${live ? ' mark--live' : ''}${className ? ` ${className}` : ''}`}
      viewBox="0 0 67 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Boardium"
    >
      <path d={WEDGE} fill={variant === 'on-navy' ? '#FFFFFF' : NAVY} />
      <path className="mark__sail" d={SAIL} fill={GOLD} />
    </svg>
  )
}
